import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Combobox, Group, Text, useCombobox } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import styles from './EditableText.module.css';

export type EditableTextSuggestOption = {
  id: string;
  label: string;
  hint?: string;
  /** Значение для сохранения, по умолчанию label */
  value?: string;
};

export type EditableTextSuggestConfig = {
  search: (query: string) => Promise<EditableTextSuggestOption[]>;
  minChars?: number;
  debounceMs?: number;
  /** Минимальная ширина выпадающего списка (px). По умолчанию 280. */
  dropdownMinWidth?: number;
};

export interface EditableTextProps {
  value: string;
  canEdit?: boolean;
  placeholder?: string;
  /** При true сразу открывает режим редактирования (для случая «добавить описание»). */
  defaultEditing?: boolean;
  onSave: (nextValue: string, selectedOption?: EditableTextSuggestOption) => void | Promise<void>;
  onCancel?: () => void;
  /** Подсказки при вводе (только для однострочного режима). */
  suggest?: EditableTextSuggestConfig;
  /** При true пустое значение сохраняется (для режима «заменить на пусто»). */
  allowEmptySave?: boolean;
  /**
   * Паттерн допустимых символов — ввод фильтруется в реальном времени.
   * Тестируется против полного значения после каждого изменения.
   * Пример: /^\d*$/ — только цифры, /^[a-zA-Z]*$/ — только латиница.
   */
  regexPattern?: RegExp;
  /**
   * Форматирует значение перед сохранением (применяется при blur/Enter).
   * Пример: форматирование телефона, trim лишних символов.
   */
  formatValue?: (value: string) => string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  maxRows?: number;
  style?: CSSProperties;
  className?: string;
}

/**
 * EditableText — инлайн‑редактирование однострочного текста по клику.
 *
 * Поведение:
 * - Клик по тексту включает режим редактирования (если canEdit).
 * - Enter — сохранить, Esc — отменить, Blur — сохранить.
 * - Пустое значение не сохраняется, отменяет редактирование.
 * - После сохранения держит оптимистичное значение (pendingValue) пока родитель
 *   не обновит проп value. Таймаут 2с — fallback если родитель не ответил.
 */
export function EditableText({
  value,
  canEdit = true,
  placeholder = '—',
  defaultEditing = false,
  onSave,
  onCancel,
  suggest,
  allowEmptySave = false,
  regexPattern,
  formatValue,
  size = 'md',
  maxRows = 1,
  style,
  className,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const [draft, setDraft] = useState(value);
  const [justSaved, setJustSaved] = useState(false);
  const [justError, setJustError] = useState(false);
  /**
   * Оптимистичное значение: показывается сразу после сохранения,
   * пока родитель не обновит проп value (ответ API).
   * Сбрасывается когда value меняется или по таймауту 2с.
   */
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  // Ref для чтения pendingValue внутри useEffect без добавления в deps
  const pendingValueRef = useRef<string | null>(null);
  pendingValueRef.current = pendingValue;
  // Запоминает цвет последней волны — обновляется при старте, не при завершении.
  // Это предотвращает зелёный «хвост» при затухании красной волны (transition 250ms).
  const lastWaveColorRef = useRef<'green' | 'red'>('green');
  if (justSaved) lastWaveColorRef.current = 'green';
  if (justError) lastWaveColorRef.current = 'red';
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(0);
  const isMultiline = maxRows > 1;
  const suggestCombobox = useCombobox();
  const skipBlurRef = useRef(false);

  const suggestEnabled = Boolean(suggest) && !isMultiline;
  const debounceMs = suggest?.debounceMs ?? 250;
  const [debouncedQuery] = useDebouncedValue(suggestEnabled ? draft : '', debounceMs);
  const [suggestions, setSuggestions] = useState<EditableTextSuggestOption[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  useEffect(() => {
    if (!suggestEnabled || !isEditing) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }
    const minChars = suggest?.minChars ?? 2;
    const query = debouncedQuery.trim();
    if (query.length < minChars) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }
    const config = suggest;
    if (!config) return;
    let cancelled = false;
    const run = async () => {
      setSuggestLoading(true);
      try {
        const items = await config.search(query);
        if (!cancelled) setSuggestions(items ?? []);
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [suggestEnabled, isEditing, debouncedQuery, suggest]);

  const suggestDropdownVisible = suggestEnabled && suggestions.length > 0;

  useEffect(() => {
    if (suggestDropdownVisible) suggestCombobox.openDropdown();
    else suggestCombobox.closeDropdown();
  }, [suggestDropdownVisible, suggestCombobox]);

  // Когда родитель меняет value — проверяем, это подтверждение сохранения или откат.
  // Если value вернулся к значению отличному от того что мы сохраняли — откат → показываем ошибку.
  useEffect(() => {
    const pending = pendingValueRef.current;
    if (pending !== null && value.trim() !== pending.trim()) {
      setJustError(true);
    }
    setPendingValue(null);
  }, [value]); // pendingValueRef читается через ref намеренно — следим только за value

  // Fallback: если родитель не обновил value за 2с — считаем это ошибкой сохранения.
  // Timeout отменяется если value успело измениться раньше (useEffect([value]) очистит pendingValue).
  useEffect(() => {
    if (pendingValue === null) return;
    const t = setTimeout(() => {
      setJustError(true);
      setPendingValue(null);
    }, 2000);
    return () => clearTimeout(t);
  }, [pendingValue]);

  // При выходе из режима редактирования и при изменении pendingValue синхронизируем draft.
  // Используем pendingValue если он есть — чтобы следующее редактирование
  // начиналось с оптимистичного значения, а не со старого value.
  // При сбросе pendingValue (в том числе по таймауту) draft возвращается к value.
  useEffect(() => {
    if (!isEditing) {
      setDraft(pendingValue ?? value);
    }
  }, [value, isEditing, pendingValue]);

  useEffect(() => {
    if (isEditing) {
      if (isMultiline) {
        // Многострочные поля (описание/примечания) — точечное редактирование,
        // курсор в конец текста, без выделения всего содержимого.
        const el = textareaRef.current;
        el?.focus();
        el?.setSelectionRange(el.value.length, el.value.length);
        return;
      }
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing, isMultiline]);

  const cancelEdit = useCallback(() => {
    setDraft(value);
    setIsEditing(false);
    onCancel?.();
  }, [value, onCancel]);

  const commitEdit = useCallback(() => {
    const trimmed = draft.trim();
    if (!allowEmptySave && !trimmed) {
      cancelEdit();
      return;
    }
    // Для suggest-полей: ненулевое значение без выбора опции → отмена.
    // Сохранить ненулевое значение можно только через commitWithOption (выбор из списка).
    if (suggestEnabled && trimmed) {
      cancelEdit();
      return;
    }
    if (trimmed === value.trim()) {
      cancelEdit();
      return;
    }
    setIsEditing(false);
    setJustSaved(true);
    setPendingValue(trimmed);
    void onSave(trimmed);
  }, [draft, value, onSave, cancelEdit, allowEmptySave, suggestEnabled]);

  const commitWithOption = useCallback(
    (option: EditableTextSuggestOption) => {
      const nextValue = (option.value ?? option.label).trim();
      setIsEditing(false);
      setJustSaved(true);
      setPendingValue(nextValue);
      suggestCombobox.closeDropdown();
      void onSave(nextValue, option);
    },
    [onSave, suggestCombobox]
  );

  const selectSuggestion = useCallback(
    (option: EditableTextSuggestOption) => {
      skipBlurRef.current = true;
      commitWithOption(option);
      setTimeout(() => {
        skipBlurRef.current = false;
      }, 0);
    },
    [commitWithOption]
  );

  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(false), 350);
    return () => clearTimeout(t);
  }, [justSaved]);

  useEffect(() => {
    if (!justError) return;
    const t = setTimeout(() => setJustError(false), 700);
    return () => clearTimeout(t);
  }, [justError]);

  const sizeStyles: Record<NonNullable<EditableTextProps['size']>, CSSProperties> = {
    xs: { fontSize: 12, lineHeight: '16px' },
    sm: { fontSize: 14, lineHeight: '18px' },
    md: { fontSize: 16, lineHeight: '20px' },
    lg: { fontSize: 18, lineHeight: '22px' },
    xl: { fontSize: 20, lineHeight: '24px' },
  };

  const sharedStyle: CSSProperties = {
    fontFamily: 'inherit',
    color: 'inherit',
    display: 'inline-block',
    verticalAlign: 'baseline',
    ...sizeStyles[size],
    ...style,
  };

  const displayValue = pendingValue ?? value;
  const formattedDisplayValue = formatValue ? formatValue(displayValue) : displayValue;
  const textToMeasure = (isEditing ? draft : formattedDisplayValue) || placeholder;

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el || isMultiline) return;
    const rect = el.getBoundingClientRect();
    if (!Number.isNaN(rect.width)) {
      setMeasuredWidth(rect.width);
    }
  }, [textToMeasure, size, style, isMultiline]);

  useLayoutEffect(() => {
    if (!isEditing || !isMultiline) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, isEditing, isMultiline, maxRows, sharedStyle.lineHeight]);

  const handleInputBlur = useCallback(() => {
    if (skipBlurRef.current) return;
    commitEdit();
  }, [commitEdit]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelEdit();
        return;
      }
      if (event.key === 'Enter') {
        if (suggestDropdownVisible) {
          event.preventDefault();
          if (suggestCombobox.getSelectedOptionIndex() < 0) suggestCombobox.selectFirstOption();
          suggestCombobox.clickSelectedOption();
          return;
        }
        if (isMultiline) return;
        event.preventDefault();
        commitEdit();
        return;
      }
    },
    [cancelEdit, suggestDropdownVisible, suggestCombobox, isMultiline, commitEdit]
  );

  const inputStyle: CSSProperties = {
    ...sharedStyle,
    width: isMultiline ? '100%' : measuredWidth ? `${measuredWidth}px` : undefined,
    minWidth: isMultiline ? undefined : measuredWidth ? `${measuredWidth}px` : undefined,
    padding: 0,
    margin: 0,
    border: 0,
    outline: 'none',
    background: 'transparent',
    resize: isMultiline ? 'none' : undefined,
    overflow: isMultiline ? 'auto' : undefined,
  };

  const handleChange = useCallback(
    (next: string) => {
      if (regexPattern && !regexPattern.test(next)) return;
      setDraft(next);
    },
    [regexPattern]
  );

  const renderInput = () =>
    isMultiline ? (
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={e => handleChange(e.currentTarget.value)}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        className={className}
        style={{ ...inputStyle, width: '100%' }}
        rows={1}
      />
    ) : (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => handleChange(e.currentTarget.value)}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        className={className}
        style={inputStyle}
      />
    );

  if (isEditing) {
    const editingContent = suggestEnabled ? (
      <Combobox
        store={suggestCombobox}
        withinPortal
        position='bottom-start'
        onOptionSubmit={optionId => {
          const option = suggestions.find(o => o.id === optionId);
          if (option) selectSuggestion(option);
        }}
      >
        <Combobox.Target>{renderInput()}</Combobox.Target>
        {suggestDropdownVisible && (
          <Combobox.Dropdown style={{ minWidth: suggest?.dropdownMinWidth ?? 280 }}>
            <Combobox.Options>
              {suggestLoading ? (
                <Combobox.Option value='__loading__' disabled>
                  <Text size='xs' c='dimmed'>
                    Загрузка...
                  </Text>
                </Combobox.Option>
              ) : (
                suggestions.map(option => (
                  <Combobox.Option
                    key={option.id}
                    value={option.id}
                    onMouseDown={() => {
                      skipBlurRef.current = true;
                      setTimeout(() => {
                        skipBlurRef.current = false;
                      }, 0);
                    }}
                  >
                    <Group gap={6} wrap='nowrap'>
                      <Text
                        size='xs'
                        style={{
                          flex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {option.label}
                      </Text>
                      {option.hint && (
                        <Text
                          size='10px'
                          c='dimmed'
                          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {option.hint}
                        </Text>
                      )}
                    </Group>
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        )}
      </Combobox>
    ) : (
      renderInput()
    );

    return (
      <>
        <span
          ref={measureRef}
          aria-hidden
          style={{
            ...sharedStyle,
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'pre',
            pointerEvents: 'none',
          }}
        >
          {textToMeasure}
        </span>
        {editingContent}
      </>
    );
  }

  return (
    <>
      <span
        ref={measureRef}
        aria-hidden
        style={{
          ...sharedStyle,
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          pointerEvents: 'none',
        }}
      >
        {textToMeasure}
      </span>
      <span
        className={[canEdit && styles.canEdit, className].filter(Boolean).join(' ') || undefined}
        onClick={canEdit ? () => setIsEditing(true) : undefined}
        style={{
          ...sharedStyle,
          width: isMultiline ? '100%' : undefined,
          cursor: canEdit ? 'text' : 'default',
          background:
            lastWaveColorRef.current === 'red'
              ? 'linear-gradient(90deg, color-mix(in srgb, var(--mantine-color-red-2) 60%, transparent), transparent)'
              : 'linear-gradient(90deg, color-mix(in srgb, var(--mantine-color-green-2) 50%, transparent), transparent)',
          backgroundSize: justSaved || justError ? '100% 100%' : '0% 100%',
          backgroundRepeat: 'no-repeat',
          borderRadius: 4,
          transition: 'background-size 0.25s ease',
        }}
      >
        {formattedDisplayValue || placeholder}
      </span>
    </>
  );
}
